import React, { useState } from "react";
import { UilDownloadAlt, UilFileUploadAlt } from "@iconscout/react-unicons";
import axios from "axios";
import { toast } from "react-toastify";
import "../add-project/addProject.css";

const BulkUpload = ({ onSuccess }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [imagesZip, setImagesZip] = useState(null);
  const token =
    localStorage.getItem("authToken") || sessionStorage.getItem("authToken");

  const handleDownloadTemplate = async () => {
    try {
      const res = await axios.get("/store/products/bulk-template", {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "store-products-template.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Template downloaded.");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to download template.");
    }
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) {
      const ext = (f.name || "").toLowerCase();
      if (!ext.endsWith(".xlsx") && !ext.endsWith(".xls")) {
        toast.error("Please select an Excel file (.xlsx or .xls).");
        return;
      }
      if (f.size > 5 * 1024 * 1024) {
        toast.error("File size must be under 5MB.");
        return;
      }
      setFile(f);
      setPreview(null);
    }
  };

  const handleZipChange = (e) => {
    const f = e.target.files?.[0];
    if (f) {
      const ext = (f.name || "").toLowerCase();
      if (!ext.endsWith(".zip")) {
        toast.error("Please select a ZIP file.");
        return;
      }
      setImagesZip(f);
    }
  };

  const handleParse = async () => {
    if (!file) {
      toast.error("Please select an Excel file first.");
      return;
    }
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post("/store/products/bulk-parse", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });
      setPreview(res.data);
      toast.success(`Parsed ${res.data.totalCount} rows.`);
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to parse file.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmInsert = async () => {
    if (!preview || preview.validCount === 0) {
      toast.error("No valid rows to insert.");
      return;
    }
    if (preview?.requiresImagesZip && !imagesZip) {
      toast.error("Please upload Images ZIP (required for new products or when photo columns are filled).");
      return;
    }
    try {
      setConfirming(true);
      const formData = new FormData();
      formData.append(
        "rows",
        JSON.stringify(preview.rows.filter((r) => r.valid))
      );
      if (imagesZip) {
        formData.append("imagesZip", imagesZip);
      }
      const res = await axios.post("/store/products/bulk-insert", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });
      if (res?.data?.failedCount > 0) {
        toast.warn(res.data.message);
      } else {
        toast.success(res.data.message);
      }
      setPreview(null);
      setFile(null);
      setImagesZip(null);
      if (onSuccess) onSuccess();
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to insert products.";
      toast.error(msg);
    } finally {
      setConfirming(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setPreview(null);
    setImagesZip(null);
  };

  return (
    <div className="bulk-upload-section">
      <h3>Add/Update products from Excel</h3>
      <p className="bulk-upload-hint">
        Download the template (11 columns), fill it, then upload for validation.
        Empty <strong>sku</strong> = new product (serial assigned automatically).
        Filled <strong>sku</strong> = update existing product.
        Columns: <strong>sku</strong>, <strong>name_en</strong>, <strong>name_ar</strong>,{" "}
        <strong>description_en</strong>, <strong>description_ar</strong>, <strong>category</strong>{" "}
        (e.g. chairs), <strong>stock</strong>, <strong>price</strong>, <strong>discount</strong>,{" "}
        <strong>main_photo</strong>, <strong>other_photos</strong> (use <strong>|</strong> for multiple).
        New products need a ZIP with matching file names. On update, leave photo columns empty to keep current images.
      </p>

      <div className="bulk-upload-actions">
        <button
          type="button"
          className="bulk-download-btn"
          onClick={handleDownloadTemplate}
        >
          <UilDownloadAlt size={20} />
          Download Template
        </button>
      </div>

      <div className="bulk-upload-form">
        <div className="bulk-upload-input-group">
          <label>Excel file (.xlsx)</label>
          <div
            className="bulk-upload-dropzone"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const f = e.dataTransfer.files?.[0];
              if (f) {
                const ext = (f.name || "").toLowerCase();
                if (ext.endsWith(".xlsx") || ext.endsWith(".xls")) {
                  setFile(f);
                  setPreview(null);
                } else {
                  toast.error("Please drop an Excel file.");
                }
              }
            }}
          >
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
            />
            {file ? (
              <span className="bulk-file-name">{file.name}</span>
            ) : (
              <>
                <UilFileUploadAlt size={32} />
                <span>Drop Excel file or click to browse</span>
              </>
            )}
          </div>
        </div>

        {preview && (
          <div className="bulk-upload-input-group">
            <label>
              Images ZIP{" "}
              {preview?.requiresImagesZip ? "(required for new products)" : "(optional)"}
            </label>
            <p className="bulk-zip-hint">
              Match <strong>main_photo</strong> and <strong>other_photos</strong> file names in the ZIP
              (example: chair.png and chair-2.png|chair-3.png).
            </p>
            <div className="bulk-upload-dropzone bulk-upload-dropzone--small">
              <input
                type="file"
                accept=".zip"
                onChange={handleZipChange}
              />
              {imagesZip ? (
                <span className="bulk-file-name">{imagesZip.name}</span>
              ) : (
                <span>Drop ZIP or click to browse</span>
              )}
            </div>
          </div>
        )}

        {!preview ? (
          <button
            type="button"
            className="bulk-parse-btn"
            onClick={handleParse}
            disabled={!file || loading}
          >
            {loading ? "Parsing..." : "Parse & Validate"}
          </button>
        ) : (
          <>
            <div className="bulk-preview-summary">
              <span className="bulk-valid">{preview.validCount} valid</span>
              <span className="bulk-invalid">{preview.invalidCount} invalid</span>
            </div>

            <div className="bulk-preview-table-wrap">
              <table className="bulk-preview-table">
                <thead>
                  <tr>
                    <th>Row</th>
                    <th>SKU</th>
                    <th>Name</th>
                    <th>Price</th>
                    <th>Category</th>
                    <th>Action</th>
                    <th>Status</th>
                    <th>Errors</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r) => (
                    <tr
                      key={r.row}
                      className={r.valid ? "bulk-row-valid" : "bulk-row-invalid"}
                    >
                      <td>{r.row}</td>
                      <td dir="ltr">{r.sku || r.data?.sku || "—"}</td>
                      <td>{r.data?.name || "—"}</td>
                      <td>{r.data?.price ?? "—"}</td>
                      <td>{r.data?.category || "—"}</td>
                      <td>{r.action || "create"}</td>
                      <td>{r.valid ? "✓ Valid" : "✗ Invalid"}</td>
                      <td>
                        {r.errors?.length ? r.errors.join("; ") : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="bulk-preview-actions">
              <button
                type="button"
                className="bulk-cancel-btn"
                onClick={handleReset}
              >
                Cancel
              </button>
              <button
                type="button"
                className="bulk-confirm-btn"
                onClick={handleConfirmInsert}
                disabled={preview.validCount === 0 || confirming || (preview?.requiresImagesZip && !imagesZip)}
              >
                {confirming ? "Inserting..." : `Confirm & Insert ${preview.validCount} product(s)`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BulkUpload;
